import { Button, ButtonGroup, Modal } from "@datapay-by-datacom/design-system";
import { Heading } from "react-aria-components";
import type { MyPayEnvironment } from "../../generated/core-api";
import styles from "./SplitCostingImportConfirmModal.module.css";

export type SplitCostingConfirmMode =
  | "replace"
  | "cancel"
  | "commit"
  | "override";

export interface SplitCostingImportConfirmModalProps {
  isOpen: boolean;
  mode: SplitCostingConfirmMode;
  rowCount: number;
  warningCount?: number;
  replacementFileName?: string;
  companyName: string;
  companyCode: string;
  environment: MyPayEnvironment;
  onSafeAction: () => void;
  onConfirmAction: () => void;
}

const COPY: Record<
  SplitCostingConfirmMode,
  { title: string; safe: string; confirm: string }
> = {
  replace: {
    title: "Replace uploaded file?",
    safe: "Keep current file",
    confirm: "Replace file",
  },
  cancel: {
    title: "Cancel import?",
    safe: "Keep importing",
    confirm: "Cancel anyway",
  },
  commit: {
    title: "Confirm import?",
    safe: "Back to review",
    confirm: "Import",
  },
  override: {
    title: "Override warnings and import?",
    safe: "Keep reviewing",
    confirm: "Import anyway",
  },
};

export function SplitCostingImportConfirmModal({
  isOpen,
  mode,
  rowCount,
  warningCount = 0,
  replacementFileName,
  companyName,
  companyCode,
  environment,
  onSafeAction,
  onConfirmAction,
}: SplitCostingImportConfirmModalProps) {
  const copy = COPY[mode];
  return (
    <Modal isOpen={isOpen}>
      <div className={styles.modalContent}>
        <Heading slot="title" className={styles.title}>
          {copy.title}
        </Heading>

        {mode === "replace" && (
          <p className={styles.message}>
            {replacementFileName} will replace {rowCount} parsed row
            {rowCount === 1 ? "" : "s"} and all validation progress.
          </p>
        )}
        {mode === "cancel" && (
          <p className={styles.message}>
            Uploaded rows and validation progress will be lost.
          </p>
        )}
        {(mode === "commit" || mode === "override") && (
          <>
            <dl className={styles.summary}>
              <dt>Rows</dt>
              <dd>{rowCount}</dd>
              <dt>Company</dt>
              <dd>
                {companyName} ({companyCode})
              </dd>
              <dt>Environment</dt>
              <dd>{environment}</dd>
              {mode === "override" && (
                <>
                  <dt>Warnings</dt>
                  <dd>{warningCount}</dd>
                </>
              )}
            </dl>
            <p className={styles.message}>
              All rows are committed together. No row is skipped or imported
              separately.
            </p>
          </>
        )}

        <div className={styles.actions}>
          <ButtonGroup>
            <Button variant="primary" onPress={onSafeAction}>
              {copy.safe}
            </Button>
            <Button variant="tertiary" onPress={onConfirmAction}>
              {copy.confirm}
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </Modal>
  );
}
